import React from 'react';
import {NavLink, useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {lobbyCasino, loghiFornitori, paginaAiuto, ricerca, sezioniAccese, skinConCasino, skinConScommesse} from '../../moduli/leggi';
import {giochi as caricaGiochi} from '../../services/casinoApi';
import {sezioneDelCatalogo} from '../Casino/comune';
import {logoFornitore} from '../Casino/fornitoriNoti';
import {indirizzoImmagine} from '../../utils/immagini';
import Registrati, {CHIUSA_PER_ORA, regoleRegistrazione} from '../Registrazione/Registrati';
import {getCurrentUsername, isPlayer} from '../../utils/currentUser';
import {haLaVoce, haUnaVoce} from '../../utils/permessi';
import {avanzamentoBonus, cifra, importo, mancaDaGiocare} from '../../utils/conto';
import BarraBonus from '../BarraBonus';
import FoglioNova from './FoglioNova';
import IconaNova, {ICONA_PAGINA, ICONA_SEZIONE} from './IconaNova';
import {pagineDelMenuAccese} from './voci';
import {fedeltaInMostra, livelloDelGiocatore, puntiTesto, useFedelta} from '../Pagine/Fedelta';
import {impostaPostoSchedina} from '../Schedina/postoSchedina';

/**
 * I PANNELLI DELLA BARRA DEL TELEFONO, uno per tasto:
 *   - GIOCHI: in cima la casella «Cerca giochi» (col modulo Ricerca giochi
 *     acceso), poi un quadratino per sezione accesa (Casinò, Live casino,
 *     Crash game…) e sotto il tasto largo d'oro «Fornitori», che apre il
 *     pannello Fornitori (con «‹ Giochi» per tornare). Le categorie non ci
 *     sono piu': stanno nella pagina di ogni sezione.
 *   - ALTRO: le pagine del modulo Pagine accese (Promozioni, Tornei, Spazio
 *     VIP, Programma fedelta', Ruota della fortuna, Hall of Fame), a tasti
 *     grandi due per riga che riempiono il pannello.
 *   - CONTO: chi e' dentro, il saldo, i quadratini del conto (Profilo,
 *     Movimenti, Lista scommesse, Cambio password, Cassa, Gioco responsabile),
 *     ognuno a chi ha la sua voce nel gettone (utils/permessi.js)
 *     ed Esci. E' quello che prima stava nel menu sotto «Il mio account».
 *   - CASSA (dal tasto Versa): il saldo e tre righe grandi, Versa, Preleva,
 *     Movimenti, ognuna a chi ha la sua voce.
 *   - REGISTRATI: con la registrazione aperta, il modulo di sempre (lo stesso
 *     della pagina /register, coi campi che decide la skin) dentro un
 *     pannello alto che scorre; chiusa, lo dice e manda all'assistenza.
 *   - FORNITORI (dal tasto largo in Giochi): una griglia di loghi, i fornitori con piu' giochi in
 *     cima, con la ricerca e, se la skin le ha entrambe, la scelta Casinò /
 *     Live casino. Toccando un logo si apre la lobby di quella sezione coi
 *     soli giochi di quel fornitore (?fornitore=…).
 * Accedi non e' qui: e' la finestra di accesso di sempre, che sul telefono
 * sale dal basso nello stesso modo.
 *
 * IL SALDO e' censurato come in testata, con lo stesso occhio: aprirlo qui lo
 * apre anche li', e viceversa.
 */

/** Il saldo con l'occhio, come in testata. */
function Saldo({conto, visibile, onOcchio, etichetta, grande}) {
  const {t} = useTranslation();
  const saldo = cifra(conto, isPlayer() ? 'userBalance' : 'available');
  if (saldo === null) return null;
  const bonus = cifra(conto, 'bonusBalance');
  const manca = mancaDaGiocare(conto);
  return (
    <div className={'nova-foglio-saldo' + (grande ? ' grande' : '')}>
      <span>
        <small>{etichetta}</small>
        <b className={visibile ? undefined : 'censurato'}>{visibile ? importo(saldo, conto && conto.currency) : '••••••'}</b>
        {bonus !== null && (
          <em className={'nova-foglio-bonus'}>
            {t('Bonus')} {visibile ? importo(bonus, conto && conto.currency) : '•••'}
            {visibile && manca > 0 && (
              <BarraBonus avanzamento={avanzamentoBonus(conto)}
                testo={t('Giocati {{giocato}} su {{requisito}}', {
                  giocato: importo(avanzamentoBonus(conto).giocato, conto && conto.currency),
                  requisito: importo(avanzamentoBonus(conto).requisito, conto && conto.currency),
                })}/>
            )}
            {visibile && manca > 0 && (
              <small>{t('Non prelevabile: gioca ancora {{importo}} per sbloccarlo', {importo: importo(manca, conto && conto.currency)})}</small>
            )}
          </em>
        )}
      </span>
      <button type={'button'} className={'nova-occhio'} aria-pressed={visibile}
        aria-label={visibile ? t('Nascondi il saldo') : t('Mostra il saldo')} onClick={onOcchio}>
        <IconaNova nome={visibile ? 'occhio' : 'occhioChiuso'} misura={18}/>
      </button>
    </div>
  );
}

/** Un quadratino: icona sopra, nome sotto. Acceso quando si e' gia' nella sua pagina. */
function Quadratino({to, icona, testo, oro, onChiudi, attivo}) {
  return (
    <NavLink to={to} onClick={onChiudi}
      className={({isActive}) => 'nova-quadro' + (oro ? ' oro' : '') + ((attivo === undefined ? isActive : attivo) ? ' active' : '')}>
      <span className={'nova-quadro-ic'}><IconaNova nome={icona}/></span>
      {testo}
    </NavLink>
  );
}

export function FoglioGiochi({aperto, onChiudi, onFornitori, conPagine}) {
  const {t} = useTranslation();
  /* Con lo sport acceso il tasto Altro lascia il posto alla Schedina
     (BarraNova): le sue pagine scendono qui, sotto i Fornitori. */
  const pagine = conPagine ? pagineDelMenuAccese(t) : [];
  const posizione = useLocation();
  const navigate = useNavigate();
  const sezioni = sezioniAccese();
  const cerca = ricerca();
  const conCerca = cerca.casella && sezioni.some((s) => s.chiave === 'casino' || s.chiave === 'liveCasino');
  const conFornitori = sezioniFornitori().length > 0;
  const [scritto, setScritto] = React.useState('');
  /* Ogni volta che si apre la casella riparte vuota. */
  React.useEffect(() => { if (aperto) setScritto(''); }, [aperto]);
  const vai = (e) => {
    e.preventDefault();
    const q = scritto.trim();
    onChiudi();
    navigate(q ? '/cerca?q=' + encodeURIComponent(q) : '/cerca');
  };

  return (
    <FoglioNova aperto={aperto} titolo={t('Giochi')} onChiudi={onChiudi}>
      {conCerca && (
        <form className={'nova-foglio-cerca nova-giochi-cerca'} role={'search'} onSubmit={vai}>
          <IconaNova nome={'lente'} misura={18}/>
          <input type={'search'} value={scritto} onChange={(e) => setScritto(e.target.value)} enterKeyHint={'search'}
            placeholder={cerca.segnaposto || t('Cerca giochi')} aria-label={cerca.segnaposto || t('Cerca giochi')}/>
        </form>
      )}
      <div className={'nova-quadri'}>
        {sezioni.map((s) => (
          <Quadratino key={s.chiave} to={s.percorso} icona={ICONA_SEZIONE[s.chiave] || 'giochi'} testo={t(s.nome)} onChiudi={onChiudi}
            attivo={posizione.pathname === s.percorso}/>
        ))}
      </div>
      {conFornitori && (
        <button type={'button'} className={'nova-foglio-largo'} onClick={onFornitori}>
          <IconaNova nome={'strati'} misura={20}/>{t('Fornitori')}<span className={'nova-foglio-largo-freccia'} aria-hidden={'true'}>›</span>
        </button>
      )}
      {pagine.length > 0 && (
        <div className={'nova-quadri nova-giochi-pagine'}>
          {pagine.map((p) => (
            <Quadratino key={p.chiave} to={p.percorso} icona={ICONA_PAGINA[p.chiave] || 'stella'} testo={p.titolo} onChiudi={onChiudi}
              attivo={posizione.pathname === p.percorso}/>
          ))}
        </div>
      )}
    </FoglioNova>
  );
}

/**
 * IL PANNELLO SCHEDINA (skin con lo sport): alto quasi quanto lo schermo, e
 * dentro la schedina vera della pagina delle quote, che ci si disegna con un
 * portale (Schedina/postoSchedina.js). Fuori dalle pagine delle quote il
 * posto resta vuoto, e al suo posto c'e' l'invito ad andare allo sport (lo
 * mostra il CSS quando il posto e' vuoto). Dopo «Gioca» il pannello resta
 * aperto: la schedina mostra il codice della giocata.
 */
export function FoglioSchedina({aperto, onChiudi, quante}) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const posto = React.useCallback((nodo) => impostaPostoSchedina(nodo), []);
  /* Chiudendo il pannello si avvisa la schedina (SchedinaCollegata): una
     prenotazione appena fatta non deve ricomparire alla prossima apertura. */
  const eraAperto = React.useRef(aperto);
  React.useEffect(() => {
    if (eraAperto.current && !aperto) window.dispatchEvent(new Event('schedinaChiusa'));
    eraAperto.current = aperto;
  }, [aperto]);
  return (
    <FoglioNova aperto={aperto} alto titolo={quante > 0 ? t('Schedina') + ' · ' + quante : t('Schedina')} onChiudi={onChiudi}>
      <div className={'nova-foglio-scorre nova-schedina'}>
        <div ref={posto} className={'nova-schedina-posto'}/>
        <div className={'nova-schedina-vuota'}>
          <IconaNova nome={'scontrino'} misura={34}/>
          <p>{quante > 0 ? t('Le tue quote ti aspettano nella pagina Sport.') : t('Tocca una quota per cominciare.')}</p>
          <button type={'button'} className={'nova-foglio-largo'} onClick={() => { onChiudi(); navigate('/sport'); }}>
            {t('Vai allo sport')}<span className={'nova-foglio-largo-freccia'} aria-hidden={'true'}>›</span>
          </button>
        </div>
      </div>
    </FoglioNova>
  );
}

/**
 * IL PANNELLO ALTRO (il secondo tasto della barra): le pagine del modulo
 * Pagine accese, coi titoli della skin, a tasti grandi due per riga. Con
 * meno pagine i tasti restano due per riga e riempiono lo stesso il
 * pannello; con un numero dispari l'ultimo prende la riga intera. Senza
 * pagine accese il tasto Altro non c'e' (BarraNova).
 */
export function FoglioAltro({aperto, onChiudi}) {
  const {t} = useTranslation();
  const pagine = pagineDelMenuAccese(t);
  return (
    <FoglioNova aperto={aperto} titolo={t('Altro')} onChiudi={onChiudi}>
      <div className={'nova-quadri nova-quadri-grandi'}>
        {pagine.map((p) => (
          <Quadratino key={p.chiave} to={p.percorso} icona={ICONA_PAGINA[p.chiave] || 'stella'} testo={p.titolo} onChiudi={onChiudi}/>
        ))}
      </div>
    </FoglioNova>
  );
}

export function FoglioConto({aperto, onChiudi, conto, saldoVisibile, onOcchio, onEsci, nome}) {
  const {t} = useTranslation();
  const chi = nome || getCurrentUsername() || '';
  /* Col programma fedelta' in mostra (Pagine/Fedelta.js), al posto di «Giocatore» il livello coi punti, che porta a /fedelta. */
  const fedelta = useFedelta(aperto && isPlayer() && fedeltaInMostra());
  const livello = fedelta.stato === 'pronto' ? livelloDelGiocatore(fedelta.dati) : null;
  return (
    <FoglioNova aperto={aperto} titolo={t('Il mio conto')} onChiudi={onChiudi}>
      <div className={'nova-foglio-chi'}>
        <span className={'nova-avatar'} aria-hidden={'true'}>{(chi[0] || '?').toUpperCase()}</span>
        <span className={'nova-foglio-nome'}>
          <b>{chi}</b>
          {livello ? (
            <NavLink to={'/fedelta'} className={'nova-foglio-livello'} onClick={onChiudi}>
              {t('Livello {{livello}} · {{punti}} punti', {livello: livello.nome, punti: puntiTesto(fedelta.dati.punti)})}
            </NavLink>
          ) : isPlayer() && <small>{t('Giocatore')}</small>}
        </span>
        <Saldo conto={conto} visibile={saldoVisibile} onOcchio={onOcchio} etichetta={isPlayer() ? t('Saldo') : t('Disp.')}/>
      </div>
      {/* Ogni quadratino a chi ha la sua voce nel gettone (utils/permessi.js),
          come nel menu delle altre skin; Gioco responsabile e' di tutti.
          Lista scommesse e Report chiedono anche la sezione della skin
          (moduli/leggi.js): la voce del gettone vale su tutte le skin. */}
      <div className={'nova-quadri'}>
        {haLaVoce('SITE_PROFILE_VIEW') && <Quadratino to={'/my-account/profilo'} icona={'tessera'} testo={t('Profilo')} onChiudi={onChiudi}/>}
        {haLaVoce('SITE_MOVEMENTS_VIEW') && <Quadratino to={'/my-account/lista-movimenti'} icona={'grafico'} testo={t('Movimenti')} onChiudi={onChiudi}/>}
        {haLaVoce('SITE_BETS_VIEW') && skinConScommesse() && <Quadratino to={'/my-account/lista-scommesse'} icona={'scontrino'} testo={t('Lista scommesse')} onChiudi={onChiudi}/>}
        {haLaVoce('SITE_PASSWORD_MANAGE') && <Quadratino to={'/my-account/sicurezza'} icona={'lucchetto'} testo={t('Cambio password')} onChiudi={onChiudi}/>}
        {haUnaVoce(['SITE_DEPOSIT', 'SITE_WITHDRAW']) && <Quadratino to={'/my-account/cassa'} icona={'portafoglio'} testo={t('Cassa')} oro onChiudi={onChiudi}/>}
        <Quadratino to={'/gioco-responsabile'} icona={'scudo'} testo={t('Gioco responsabile')} onChiudi={onChiudi}/>
        {/* La rete, per chi ha qualcuno sotto: le stesse voci della colonna del conto. */}
        {haLaVoce('SITE_NETWORK_VIEW') && <Quadratino to={'/my-account/lista-utenti'} icona={'rete'} testo={t('Lista utenti')} onChiudi={onChiudi}/>}
        {haLaVoce('SITE_TRANSFER') && <Quadratino to={'/my-account/giroconto'} icona={'scambio'} testo={t('Giroconto')} onChiudi={onChiudi}/>}
        {haLaVoce('SITE_REPORTS_VIEW') && skinConCasino() && <Quadratino to={'/my-account/report'} icona={'rapporto'} testo={t('Report')} onChiudi={onChiudi}/>}
      </div>
      <button type={'button'} className={'nova-foglio-esci'} onClick={() => { onChiudi(); onEsci(); }}>
        <IconaNova nome={'esci'} misura={18}/>{t('Esci')}
      </button>
    </FoglioNova>
  );
}

export function FoglioCassa({aperto, onChiudi, conto, saldoVisibile, onOcchio}) {
  const {t} = useTranslation();
  const posizione = useLocation();
  const inCassa = posizione.pathname === '/my-account/cassa';
  const preleva = inCassa && new URLSearchParams(posizione.search).get('verso') === 'preleva';
  const riga = (to, icona, testo, sotto, oro, acceso) => (
    <NavLink to={to} onClick={onChiudi} className={'nova-riga' + (oro ? ' oro' : '') + (acceso ? ' active' : '')}>
      <span className={'nova-quadro-ic'}><IconaNova nome={icona}/></span>
      <span>{testo}<small>{sotto}</small></span>
    </NavLink>
  );
  return (
    <FoglioNova aperto={aperto} titolo={t('Cassa')} onChiudi={onChiudi}>
      <Saldo conto={conto} visibile={saldoVisibile} onOcchio={onOcchio} grande
        etichetta={isPlayer() ? t('Saldo disponibile') : t('Disp.')}/>
      <div className={'nova-righe'}>
        {haLaVoce('SITE_DEPOSIT') && riga('/my-account/cassa', 'giu', t('Versa'), t('Ricarica il conto in criptovaluta'), true, inCassa && !preleva)}
        {haLaVoce('SITE_WITHDRAW') && riga('/my-account/cassa?verso=preleva', 'su', t('Preleva'), t('Ritira le vincite sul tuo portafoglio'), false, preleva)}
        {haLaVoce('SITE_MOVEMENTS_VIEW') && riga('/my-account/lista-movimenti', 'grafico', t('Movimenti'), t('Versamenti, prelievi e giocate'), false,
          posizione.pathname === '/my-account/lista-movimenti')}
      </div>
    </FoglioNova>
  );
}

export function FoglioRegistrati({aperto, onChiudi, onAccedi, userService, ricerca, telegram, avviso}) {
  const {t} = useTranslation();
  const assistenza = paginaAiuto('assistenza');
  if (regoleRegistrazione().aperta) {
    return (
      <FoglioNova aperto={aperto} titolo={t('Apri il tuo conto')} onChiudi={onChiudi} alto finestra>
        <div className={'nova-foglio-scorre'}>
          {/* Montato solo da aperto: richiudendo e riaprendo si riparte da un modulo pulito. */}
          {aperto && <Registrati userService={userService} nelFoglio onAccedi={onAccedi} onChiudi={onChiudi} ricerca={ricerca || ''}
            telegramIniziale={telegram || null} avvisoTelegram={!!avviso}/>}
        </div>
      </FoglioNova>
    );
  }
  return (
    <FoglioNova aperto={aperto} titolo={t('Registrati')} onChiudi={onChiudi} finestra>
      <div className={'nova-foglio-avviso'}>
        <span className={'nova-quadro-ic'}><IconaNova nome={'nuovoUtente'}/></span>
        <p>{t(CHIUSA_PER_ORA)}</p>
      </div>
      {assistenza && assistenza.acceso && (
        <NavLink to={assistenza.percorso} onClick={onChiudi} className={'nova-foglio-btn'}>
          <IconaNova nome={'fumetto'} misura={18}/>{t('Scrivi all’assistenza')}
        </NavLink>
      )}
      <p className={'nova-foglio-sotto'}>
        {t('Hai già un conto?')}{' '}
        <button type={'button'} onClick={onAccedi}>{t('Accedi')}</button>
      </p>
    </FoglioNova>
  );
}

/**
 * Le sezioni che hanno un elenco di fornitori da mostrare: Casinò e Live
 * casino, se sono accese e se la loro lobby ha il filtro per fornitore
 * (Moduli › Casinò › Fornitori): senza, il logo aprirebbe la lobby intera.
 * Vuoto = nel pannello Giochi il tasto «Fornitori» non c'e'.
 */
export function sezioniFornitori() {
  const accese = sezioniAccese();
  return ['casino', 'liveCasino']
      .map((k) => accese.find((s) => s.chiave === k))
      .filter((s) => s && lobbyCasino(s.chiave).fornitori);
}

/** Il colore del riquadro di un fornitore senza logo: sempre lo stesso per lo stesso nome. */
function coloreDi(nome) {
  let h = 0;
  for (let i = 0; i < nome.length; i += 1) h = (h * 31 + nome.charCodeAt(i)) % 360;
  return `hsl(${h} 55% 36%)`;
}
function iniziali(nome) {
  return nome.replace(/[^\p{L}\p{N} ]/gu, '').split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';
}

export function FoglioFornitori({aperto, onChiudi, onIndietro}) {
  const {t} = useTranslation();
  const posizione = useLocation();
  const sezioni = sezioniFornitori();
  const [chiave, setChiave] = React.useState(null);
  const sezione = sezioni.find((s) => s.chiave === chiave) || sezioni[0] || null;
  const [cerca, setCerca] = React.useState('');
  /* {chiave: [{nome, quanti}]}: null mentre si carica, [] se non arriva niente. */
  const [elenchi, setElenchi] = React.useState({});

  /* Aprendo, la sezione e' quella della pagina in cui si e' (Live casino dal Live casino). */
  React.useEffect(() => {
    if (!aperto) return;
    const qui = sezioni.find((s) => posizione.pathname === s.percorso || posizione.pathname.startsWith(s.percorso + '/'));
    if (qui) setChiave(qui.chiave);
    setCerca('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aperto]);

  const k = sezione && sezione.chiave;
  React.useEffect(() => {
    if (!aperto || !k || elenchi[k] !== undefined) return undefined;
    let vivo = true;
    setElenchi((e) => Object.assign({}, e, {[k]: null}));
    caricaGiochi(sezioneDelCatalogo(k))
        .then((giochi) => {
          const conti = new Map();
          giochi.forEach((g) => { if (g.fornitore) conti.set(g.fornitore, (conti.get(g.fornitore) || 0) + 1); });
          const lista = Array.from(conti, ([nome, quanti]) => ({nome, quanti}))
              .sort((a, b) => b.quanti - a.quanti || a.nome.localeCompare(b.nome));
          if (vivo) setElenchi((e) => Object.assign({}, e, {[k]: lista}));
        })
        .catch(() => vivo && setElenchi((e) => Object.assign({}, e, {[k]: []})));
    return () => { vivo = false; };
  }, [aperto, k, elenchi]);

  const loghi = loghiFornitori();
  const elenco = k ? elenchi[k] : [];
  const testo = cerca.trim().toLowerCase();
  const mostrati = (elenco || []).filter((f) => !testo || f.nome.toLowerCase().includes(testo));
  const scelto = sezione && posizione.pathname === sezione.percorso ? new URLSearchParams(posizione.search).get('fornitore') : null;

  return (
    <FoglioNova aperto={aperto} titolo={t('Fornitori')} onChiudi={onChiudi} alto
      indietro={onIndietro ? {testo: t('Giochi'), onClick: onIndietro} : undefined}>
      {sezioni.length > 1 && (
        <div className={'nova-segmenti'} role={'tablist'}>
          {sezioni.map((s) => (
            <button key={s.chiave} type={'button'} role={'tab'} aria-selected={s.chiave === k}
              className={s.chiave === k ? 'on' : undefined} onClick={() => setChiave(s.chiave)}>{t(s.nome)}</button>
          ))}
        </div>
      )}
      <label className={'nova-foglio-cerca'}>
        <IconaNova nome={'lente'} misura={18}/>
        <input type={'search'} value={cerca} onChange={(e) => setCerca(e.target.value)}
          placeholder={t('Cerca un fornitore')} aria-label={t('Cerca un fornitore')}/>
      </label>
      <div className={'nova-foglio-scorre'}>
        {elenco === null || elenco === undefined ? (
          <div className={'nova-loghi'} aria-hidden={'true'}>
            {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className={'nova-logo-carta vuota'}><i/></span>)}
          </div>
        ) : mostrati.length === 0 ? (
          <p className={'nova-foglio-vuoto'}>{testo ? t('Nessun fornitore con questo nome.') : t('Nessun fornitore da mostrare.')}</p>
        ) : (
          <div className={'nova-loghi'}>
            {mostrati.map((f) => {
              /* Il logo caricato nel backoffice, o quello di serie di un fornitore noto (fornitoriNoti.js). */
              const logo = logoFornitore(f.nome, loghi);
              return (
                <NavLink key={f.nome} to={sezione.percorso + '?fornitore=' + encodeURIComponent(f.nome)} onClick={onChiudi}
                  className={'nova-logo-carta' + (scelto === f.nome ? ' active' : '')}>
                  {logo
                    ? <span className={'nova-logo-img'}><img src={indirizzoImmagine(logo)} alt={''} loading={'lazy'}/></span>
                    : <span className={'nova-logo-img'} style={{background: coloreDi(f.nome)}} aria-hidden={'true'}>{iniziali(f.nome)}</span>}
                  <b>{f.nome}</b>
                  <small>{t('{{count}} giochi', {count: f.quanti})}</small>
                </NavLink>
              );
            })}
          </div>
        )}
      </div>
    </FoglioNova>
  );
}
